// "The staff session ended": the single owner of that state (owner decision
// 2026-10-12, auth-rbac.md quirk 7). Any 401 on an authenticated staff call
// clears the stored session, and the admin route guard (admin-v2 `Gate`)
// REPLACE-navigates to /admin/login?next=<the page>. The call that got the 401
// never settles, so no page renders an error state on the way out.
//
// No React and nothing from admin-v2: services/api.ts, AuthContext, the hooks
// and the kit all read it (admin-shell.md quirk 23: the kit cannot call useAuth).
//
// Anti-loop rules: never act while on a staff auth page; the login, invite,
// forgot / reset password, sign-in handoff and public calls keep their own
// 401 meaning (wrong password, a dead link); a 401 for a token that is no
// longer the stored one is ignored (another tab signed in, quirk 28).

export const SESSION_ENDED_NOTICE = 'Your session ended. Sign in to pick up where you left off.'

const TOKEN_KEY = 'mcl_admin_token'
const USER_KEY = 'mcl_admin_user'

/** The signed-out staff pages. Case-insensitive, as React Router matches routes. */
export function isStaffAuthPath(pathname: string): boolean {
  return /^\/admin\/(login|impersonate|forgot-password|invite|reset-password|handoff)(\/|$)/i.test(pathname)
}

let ended: { email: string | null } | null = null
// The token of the session that ended: a late 401 for it never settles either.
let endedToken: string | null = null
const listeners = new Set<() => void>()
// Whose unsaved work (queued saves with Retry, Undo toasts, box score drafts)
// the tab still holds since a session ended. Set with the session end, read
// and cleared by the next sign-in (staffSignedIn). userId null: unknown.
let workOwner: { userId: number | null } | null = null
const discardListeners = new Set<() => void>()

/** Read without consuming, so StrictMode's double effect keeps the notice. */
export function getStaffSessionEnd(): { email: string | null } | null {
  return ended
}

/**
 * Remember that the stored session ended: its email (for the login page) and
 * its token. Call it before the stored session is cleared.
 */
export function markStaffSessionEnded(): void {
  if (ended) return
  ended = { email: storedEmail() }
  endedToken = storedToken()
  workOwner = { userId: storedUserId() }
}

/**
 * AuthContext.login: someone signed in. When a session ended earlier in this
 * tab and the person signing in is not the one whose session it was (or that
 * is unknown), the work it left behind is discarded: a Retry or an Undo would
 * replay their writes under this person's token (audit logs would credit the
 * wrong staff user). The same person keeps it, Retry included.
 */
export function staffSignedIn(userId: number): void {
  const owner = workOwner
  workOwner = null
  if (!owner || (owner.userId !== null && owner.userId === Number(userId))) return
  for (const fn of Array.from(discardListeners)) {
    try {
      fn()
    } catch {
      /* one listener's failure must not stop the others */
    }
  }
}

/** Called when a sign-in discards the work a different person's session left. */
export function onStaffWorkDiscarded(fn: () => void): () => void {
  discardListeners.add(fn)
  return () => {
    discardListeners.delete(fn)
  }
}

export function clearStaffSessionEnd(): void {
  ended = null
  endedToken = null
  workOwner = null
}

export function onStaffSessionEnded(fn: () => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

/** What a call whose 401 ended the session returns: it never settles. */
export function neverSettles<T>(): Promise<T> {
  return new Promise<T>(() => {})
}

function storedEmail(): string | null {
  try {
    const raw = localStorage.getItem(USER_KEY)
    if (!raw) return null
    const u = JSON.parse(raw) as { email?: unknown } | null
    return u && typeof u.email === 'string' ? u.email : null
  } catch {
    return null
  }
}

function storedUserId(): number | null {
  try {
    const raw = localStorage.getItem(USER_KEY)
    if (!raw) return null
    const u = JSON.parse(raw) as { id?: unknown } | null
    const id = Number(u?.id)
    return u && u.id != null && Number.isFinite(id) ? id : null
  } catch {
    return null
  }
}

/**
 * The stored staff token, read only (writes nothing, ends nothing). For the
 * public client on a members-only league only (services/publicAccess.ts,
 * registration step 33): a staff session on the same origin admits its
 * holder to the private site. The public client never clears or ends it.
 */
export function readStaffToken(): string | null {
  return storedToken()
}

function storedToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

/**
 * End the staff session: clear the stored token and user (the clock offset,
 * `mcl_clock_offset_ms`, stays: frontend-api-client.md quirk 13), remember the
 * email for the login page, and tell the listeners (AuthContext drops its
 * in-memory session; the admin Gate then navigates). Fires once.
 * Returns false while on a staff auth page, where the caller throws as before.
 */
export function endStaffSession(): boolean {
  if (typeof window !== 'undefined' && isStaffAuthPath(window.location.pathname)) return false
  if (ended) return true
  markStaffSessionEnded()
  try {
    localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(USER_KEY)
  } catch {
    /* ignore */
  }
  for (const fn of Array.from(listeners)) {
    try {
      fn()
    } catch {
      /* a listener's failure must not stop the others */
    }
  }
  return true
}

/**
 * A 401 answered `target` (an API path or URL) that was sent with `sentToken`.
 * True when it ended the session (the caller then returns `neverSettles()`);
 * false when the caller should throw as before: the exempt calls (login,
 * invite, forgot-password, reset-password, the sign-in handoff, public), a
 * stale token (a newer session is stored now),
 * or an auth page. A late 401 for the session that already ended is true
 * wherever the page is now: it may land after Gate moved to /admin/login, and
 * it must not reach the module-level save and toast stores, which outlive the
 * session.
 */
export function staffSessionEndedBy401(target: string, sentToken: string | null): boolean {
  if (/\/admin\/auth\/(login|invite|forgot-password|reset-password)(\/|\?|$)/.test(target) || /\/auth\/handoff(\/|\?|$)/.test(target) || /\/public\//.test(target)) return false
  if (ended && sentToken !== null && sentToken === endedToken) return true
  const now = storedToken()
  if (now !== null && now !== sentToken) return false
  return endStaffSession()
}
